import styled from 'styled-components'
import DateInput from '../../../../components/editors/input/DateInput'
import { memo, useCallback, useEffect, useState } from 'react'
import { DateTermVO } from '../../../../../domain/value-object/DateTermVO'
import { Box, ClickAwayListener, IconButton, Typography } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import {
  DateVO,
  dateVoService,
} from '../../../../../domain/value-object/DateVO'
import CalendarIcon from '../../../../components/icons/CalendarIcon'
import ClearIcon from '../../../../components/icons/ClearIcon'
import { KEY_ESC, KEY_SAVE } from '../../../../model/keyBind'
import store from '../../../../../store'
import {
  addKeyBindListeners,
  removeKeyBindListeners,
} from '../../../../../store/keyBind'

export type Props = {
  value?: DateTermVO
  onChange: (value?: DateTermVO) => void
  onOpen?: () => void
  onClose?: () => void
}

const KEY_BIND_ID: string = 'KANBAN_CARD_DATERANGE_KEY_BIND_ID'

export const DateRangeItem = memo(
  ({ value, onChange, onOpen, onClose }: Props) => {
    const [isEditingFrom, setIsEditingFrom] = useState(false)
    const [isEditingTo, setIsEditingTo] = useState(false)
    const onChangeFrom = useCallback(
      (v: DateVO | undefined) => {
        if (!v && !value?.to) {
          onChange(undefined)
          return
        }
        const shiftedTo = isStartDateBeyondEndDate(v, value?.to) ? v : value?.to
        onChange({ from: v, to: shiftedTo } as DateTermVO)
      },
      [onChange, value]
    )

    const onChangeTo = useCallback(
      (v: DateVO | undefined) => {
        if (!v && !value?.from) {
          onChange(undefined)
          return
        }
        const shiftedFrom = isStartDateBeyondEndDate(value?.from, v)
          ? v
          : value?.from
        onChange({ from: shiftedFrom, to: v } as DateTermVO)
      },
      [onChange, value]
    )

    const isStartDateBeyondEndDate = useCallback(
      (from?: DateVO, to?: DateVO) =>
        !!from && !!to && dateVoService.diff(from, to) > 0,
      []
    )

    const quitEditingBySpecialKey = useCallback(
      e => {
        if (e.key === 'Escape' || e.key === 'Enter') {
          isEditingFrom && setIsEditingFrom(false)
          isEditingTo && setIsEditingTo(false)
        }
      },
      [isEditingFrom, isEditingTo]
    )

    const isEditing = isEditingFrom || isEditingTo

    const startEditingFrom = useCallback(() => {
      onOpen?.()
      setIsEditingFrom(true)
    }, [onOpen])

    const startEditingTo = useCallback(() => {
      onOpen?.()
      setIsEditingTo(true)
    }, [onOpen])

    const stopEditingFrom = useCallback(() => {
      setIsEditingFrom(false)
      // isEditingToがfalseの場合のみonCloseを呼ぶ
      if (!isEditingTo) {
        onClose?.()
      }
    }, [isEditingTo, onClose])

    const stopEditingTo = useCallback(() => {
      setIsEditingTo(false)
      // isEditingFromがfalseの場合のみonCloseを呼ぶ
      if (!isEditingFrom) {
        onClose?.()
      }
    }, [isEditingFrom, onClose])

    const clearFrom = useCallback(() => {
      onChangeFrom(undefined)
    }, [onChangeFrom])

    const clearTo = useCallback(() => {
      onChangeTo(undefined)
    }, [onChangeTo])

    useEffect(() => {
      if (isEditing) {
        store.dispatch(
          addKeyBindListeners(
            [
              {
                key: KEY_SAVE,
                fn: () => {
                  // stop default Ctrl + s behavior
                },
                stopDefaultBehavior: true,
              },
              { key: KEY_ESC, fn: quitEditingBySpecialKey },
            ],
            KEY_BIND_ID
          )
        )
      } else {
        store.dispatch(removeKeyBindListeners(KEY_BIND_ID))
      }
    }, [isEditing, quitEditingBySpecialKey])

    return (
      <RootDiv>
        <Box sx={{ padding: '0 2px 0 2px', width: '108px' }}>
          {isEditingFrom ? (
            <ClickAwayListener onClickAway={stopEditingFrom}>
              <StyledDateInput
                value={value?.from}
                onChange={onChangeFrom}
                placeholder={'YYYY/MM/DD'}
                enterHandler={stopEditingFrom}
                defaultEditing={true}
                onEndEditing={stopEditingFrom}
              />
            </ClickAwayListener>
          ) : (
            <StyledDateRenderer>
              <StyledDateTypography
                hasValue={!!value?.from}
                onClick={startEditingFrom}
              >
                {value?.from
                  ? dateVoService.format(value?.from, 'YYYY/MM/DD')
                  : 'YYYY/MM/DD'}
              </StyledDateTypography>
              {value?.from && (
                <StyledClearIconButton onClick={clearFrom}>
                  <ClearIcon />
                </StyledClearIconButton>
              )}
              <StyledCalendarIcon onClick={startEditingFrom}>
                <CalendarIcon />
              </StyledCalendarIcon>
            </StyledDateRenderer>
          )}
        </Box>
        <Typography
          sx={{ margin: 'auto 10px', color: colorPalette.monotone[3] }}
        >
          ～
        </Typography>
        <Box sx={{ padding: '0 2px 0 2px', width: '108px' }}>
          {isEditingTo ? (
            <ClickAwayListener onClickAway={stopEditingTo}>
              <StyledDateInput
                value={value?.to}
                onChange={onChangeTo}
                placeholder={'YYYY/MM/DD'}
                enterHandler={stopEditingTo}
                defaultEditing={true}
                onEndEditing={stopEditingTo}
              />
            </ClickAwayListener>
          ) : (
            <StyledDateRenderer>
              <StyledDateTypography
                hasValue={!!value?.to}
                onClick={startEditingTo}
              >
                {value?.to
                  ? dateVoService.format(value?.to, 'YYYY/MM/DD')
                  : 'YYYY/MM/DD'}
              </StyledDateTypography>
              {value?.to && (
                <StyledClearIconButton onClick={clearTo}>
                  <ClearIcon />
                </StyledClearIconButton>
              )}
              <StyledCalendarIcon onClick={startEditingTo}>
                <CalendarIcon />
              </StyledCalendarIcon>
            </StyledDateRenderer>
          )}
        </Box>
      </RootDiv>
    )
  }
)

const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  '& > .react-datepicker__tab-loop': {
    width: 0,
  },
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
})

const StyledDateInput = styled(DateInput)({
  '& .MuiInputBase-input': {
    width: '80px',
    color: colorPalette.monotone[5],
  },
})

const StyledDateRenderer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const StyledDateTypography = styled(Typography)<{ hasValue: boolean }>(
  props => ({
    color: props.hasValue ? colorPalette.monotone[5] : colorPalette.monotone[2],
    width: '80px',
    cursor: 'pointer',
  })
)

const StyledClearIconButton = styled(IconButton)({
  padding: '2px',
})

const StyledCalendarIcon = styled('div')({
  padding: '5px 5px 0',
  cursor: 'pointer',
})
